/**
 * spaceBgDecorations.ts
 * Pure builder for the World 2 mid-distance backdrop decoration layer.
 *
 * Procedural (no PNGs) — each entry carries enough information for
 * the renderers (`SpaceLayer.tsx` for Skia, `webSpaceDraw.web.ts` for
 * Canvas2D) to draw a varied celestial body using primitives:
 *
 *   - body radius + fill colour + lit-limb highlight (every kind)
 *   - optional ring (ring colour + ring half-width + ring half-height)
 *   - optional debris cluster (6..10 small chunks scattered around the
 *     centre — used in place of the body for the `exploded` kind)
 *
 * The builder is fully deterministic: given the same `cameraX` it
 * always returns the same list. Per-bucket kind / position / colour
 * / ring tilt is derived from a cheap integer hash of the bucket
 * index, so the user sees the same universe every replay and a unit
 * test can pin exact coordinates without mocking `Math.random`.
 *
 * Replaces the prior PNG-based planet/moon/star sprites — the single
 * planet asset read as visually repetitive even with horizontal flip.
 * Seven shape variants (small / medium / giant / ringed / dwarf /
 * exploded / star) with hash-derived hue rotation gives clearly
 * different bodies every screen without any new art.
 *
 * Output is camera-relative — each entry's centre is already
 * `worldX - cameraX * SPACE_BG_DECORATION_PARALLAX_FACTOR`. Entries
 * fully outside the viewport (with a one-bucket margin) are culled
 * before they leave the builder so the renderer's per-decoration loop
 * is bounded by `⌈VIEWPORT_WIDTH / BUCKET_WIDTH⌉ + 2`.
 *
 * No game-state mutation. No React. No image imports.
 *
 * Phase 4 — World 2: Starfield Drift (overhaul pass).
 *
 * @module spaceBgDecorations
 */

import { spaceState } from '../engine/spaceState';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { SPACE_BG_DECORATION_PARALLAX_FACTOR } from '../../constants/rendering';
import type {
  SpaceBgDecoration,
  SpaceBgDecorationKind,
} from './spaceBgDecorationTypes';
import {
  buildDebris,
  buildRing,
  colorForKind,
  hash32,
  highlightForKind,
  kindForBucket,
  radiusForKind,
} from './spaceBgDecorationCatalog';

export type {
  SpaceBgDecoration,
  SpaceBgDecorationKind,
  SpaceBgDebrisChunk,
} from './spaceBgDecorationTypes';

/**
 * Width of one decoration bucket along the world X axis. Each bucket
 * emits at most one decoration; bucket index drives the deterministic
 * hash. Set to half the viewport width so the screen often carries two
 * decorations on it at once — dense enough to feel "alive", sparse
 * enough that the parallax plane stays distinct from the playfield.
 *
 * DECISION: Halved from the prior `VIEWPORT_WIDTH` so the screen never
 * goes empty when one bucket happens to roll an `exploded` entry
 * (smaller visual footprint than a planet).
 */
export const SPACE_BG_DECORATION_BUCKET_WIDTH = Math.floor(VIEWPORT_WIDTH / 2);

/**
 * Visual half-width used for viewport culling. The body radius is fine
 * for round bodies, but `planet-ringed` extends out to `radius * 2.4`
 * and `exploded` carries debris in a ~28 px radius around the centre.
 * Using the body radius alone caused ringed planets to pop off-screen
 * early — the ring was still visible while `cx + radius < 0`.
 *
 * @param kind - The decoration kind.
 * @param radius - The body radius.
 * @returns The maximum half-width to honour when culling against the
 *   viewport edges.
 */
function cullHalfWidthForKind(
  kind: SpaceBgDecorationKind,
  radius: number,
): number {
  switch (kind) {
    case 'planet-ringed':
      // Ring extends to radius * 2.4 along X (see `buildRing`).
      return radius * 2.4;
    case 'exploded':
      // Debris dist max is ~26 px from centre + ~4 px chunk radius.
      return 30;
    default:
      return radius;
  }
}

// DECISION: Re-export under a public name so the culling tests pin
// against the same function as production. Inlining the bounds in the
// test allowed the `planet-ringed` ring width to drift out of sync
// (PR #124 review).
export { cullHalfWidthForKind as spaceBgDecorationCullHalfWidth };

/**
 * Build the camera-relative decoration list. Returns an empty array
 * when the scene is not running so the renderer can call this every
 * frame without a `spaceState.isRunning` guard of its own.
 *
 * @returns Decoration draw entries, viewport-culled and ready to
 *   paint. Empty array when the space scene is idle.
 */
export function buildSpaceBgDecorations(): SpaceBgDecoration[] {
  if (!spaceState.isRunning) return [];

  const cameraX = spaceState.cameraX;
  const parallaxX = cameraX * SPACE_BG_DECORATION_PARALLAX_FACTOR;
  const bucketWidth = SPACE_BG_DECORATION_BUCKET_WIDTH;

  // Visible world range in *decoration space* (already scaled by the
  // parallax factor). Walk one bucket either side so a decoration
  // straddling a bucket boundary is not popped mid-screen.
  const firstBucket = Math.floor(parallaxX / bucketWidth) - 1;
  const lastBucket = Math.floor((parallaxX + VIEWPORT_WIDTH) / bucketWidth) + 1;

  const out: SpaceBgDecoration[] = [];
  for (let bucket = firstBucket; bucket <= lastBucket; bucket += 1) {
    const kind = kindForBucket(bucket);
    const posHash = hash32(bucket);
    const radius = radiusForKind(kind, posHash);

    // Position the centre inside the bucket.
    const xOffsetInBucket = posHash % bucketWidth;
    const worldX = bucket * bucketWidth + xOffsetInBucket;
    const cx = worldX - parallaxX;

    // Y position spans the full viewport minus the body radius (or a
    // 24 px margin for the `exploded` kind so the debris cluster
    // doesn't graze the edge). Use the high bits of the hash so Y
    // and X are independent.
    const margin = kind === 'exploded' ? 24 : radius;
    const yRange = Math.max(1, VIEWPORT_HEIGHT - margin * 2);
    const cy = margin + ((posHash >>> 16) % yRange);

    // Viewport-cull: drop entries whose body / ring / debris bound is
    // fully outside the visible strip. Using the kind-aware half-width
    // is what prevents the ringed-planet early-pop bug — the ring
    // extends well past the body radius.
    const cullHalfWidth = cullHalfWidthForKind(kind, radius);
    if (cx + cullHalfWidth < 0 || cx - cullHalfWidth > VIEWPORT_WIDTH) continue;

    const entry: SpaceBgDecoration = {
      cx,
      cy,
      kind,
      radius,
      color: colorForKind(kind, posHash),
      highlight: highlightForKind(kind),
      key: `bgd${bucket}`,
    };

    if (kind === 'planet-ringed') {
      entry.ring = buildRing(posHash, radius);
    } else if (kind === 'exploded') {
      entry.debris = buildDebris(bucket, posHash);
    }

    out.push(entry);
  }

  return out;
}
